import React from "react";
import Card from "components/card";
import { getUser, saveUser } from "auth/Authentication";
import {
  THEME_OPTIONS,
  getDefaultColorsForTheme,
  applyTheme,
  buildCustomCssVars,
  ThemeMode,
} from "theme/themeData";
import { updatetDataById } from "api";
import { users } from "api/collection_names";
import AppSnackbar from "components/snakbar";
import { SnackbarState, CustomThemeColors, CustomThemeModes } from "models/user-models";
import CircularProgress from "@mui/material/CircularProgress";
import { Modal, Box } from "@mui/material";
import { MdCheckCircle, MdPalette, MdRestore } from "react-icons/md";
import { HiX } from "react-icons/hi";

const COLOR_PICKER_FIELDS: {
  key: keyof CustomThemeColors;
  label: string;
  defaultVal: string;
}[] = [
  { key: "primary", label: "Primary Accent", defaultVal: "#422AFB" },
  { key: "secondary", label: "Secondary Accent", defaultVal: "#7551FF" },
  { key: "bg", label: "App Background", defaultVal: "#F4F7FE" },
  { key: "foreground", label: "App Foreground", defaultVal: "#ffffff" },
  { key: "textPrimary", label: "Main Text Color", defaultVal: "#1b2559" },
];

const ChangeTheme = () => {
  const [currentUser, setCurrentUser] = React.useState<any>(null);
  const [activeTheme, setActiveTheme] = React.useState<string>("horizonDefault");
  const [themeMode, setThemeMode] = React.useState<ThemeMode>("light");
  const [useCustomTheme, setUseCustomTheme] = React.useState<boolean>(false);
  const [isCustomizerOpen, setIsCustomizerOpen] = React.useState<boolean>(false);
  const [customColors, setCustomColors] = React.useState<CustomThemeColors>(
    getDefaultColorsForTheme("horizonDefault")
  );
  const [loadingTheme, setLoadingTheme] = React.useState<boolean>(false);
  const [snackbar, setSnackbar] = React.useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  React.useEffect(() => {
    const user = getUser();
    if (user) {
      setCurrentUser(user);
      const themeId = user.theme || "horizonDefault";
      const mode: ThemeMode = user.themeMode || "light";
      setActiveTheme(themeId);
      setThemeMode(mode);
      setUseCustomTheme(!!user.useCustomTheme);

      const savedTheme = user.customTheme as CustomThemeColors | CustomThemeModes | undefined;
      const savedColors = savedTheme && ("light" in savedTheme || "dark" in savedTheme)
        ? savedTheme[mode]
        : savedTheme;
      const baseThemeColors = getDefaultColorsForTheme(themeId, mode);
      const mergedColors: CustomThemeColors = {
        ...baseThemeColors,
        ...(savedColors || {}),
      };
      setCustomColors(mergedColors);
    }
  }, []);

  // When user selects a preset theme card
  const handleThemeSelect = async (themeId: string) => {
    if (!currentUser) return;
    setLoadingTheme(true);

    const defaultColorsForTheme = getDefaultColorsForTheme(themeId, themeMode);

    const updatedUser = {
      ...currentUser,
      theme: themeId,
      themeMode,
      useCustomTheme: false,
      customTheme: {
        light: getDefaultColorsForTheme(themeId, "light"),
        dark: getDefaultColorsForTheme(themeId, "dark"),
      },
    };

    try {
      const res = await updatetDataById(users, updatedUser, currentUser._id);
      if (res.data) {
        saveUser(updatedUser);
        setCurrentUser(updatedUser);
        setActiveTheme(themeId);
        setUseCustomTheme(false);
        setCustomColors(defaultColorsForTheme);
        applyTheme(themeId, undefined, themeMode);

        setSnackbar({
          open: true,
          message: `${
            THEME_OPTIONS.find((t) => t.id === themeId)?.name
          } Preset Applied!`,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to update theme in database.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error setting theme:", error);
      setSnackbar({
        open: true,
        message: "An error occurred while updating theme.",
        severity: "error",
      });
    } finally {
      setLoadingTheme(false);
    }
  };

  // When user modifies a color in the color picker
  const handleColorChange = (key: keyof CustomThemeColors, value: string) => {
    const updatedColors = { ...customColors, [key]: value };
    setCustomColors(updatedColors);

    // Apply live preview locally
    const customCssVars = buildCustomCssVars(updatedColors, themeMode);
    applyTheme(activeTheme, customCssVars, themeMode);
  };

  const handleModeChange = async (mode: ThemeMode) => {
    setThemeMode(mode);
    const savedTheme = currentUser?.customTheme as CustomThemeColors | CustomThemeModes | undefined;
    const savedColors = savedTheme && ("light" in savedTheme || "dark" in savedTheme)
      ? savedTheme[mode]
      : undefined;
    const nextColors = savedColors || getDefaultColorsForTheme(activeTheme, mode);
    setCustomColors(nextColors);
    const customCssVars = useCustomTheme ? buildCustomCssVars(nextColors, mode) : undefined;
    applyTheme(activeTheme, customCssVars, mode);

    if (currentUser) {
      const updatedUser = { ...currentUser, themeMode: mode };
      saveUser(updatedUser);
      setCurrentUser(updatedUser);
      try {
        await updatetDataById(users, updatedUser, currentUser._id);
      } catch (error) {
        console.error("Error saving theme mode:", error);
      }
    }
  };

  // Save custom colors to backend
  const handleSaveCustomTheme = async () => {
    if (!currentUser) return;
    setLoadingTheme(true);

    const updatedUser = {
      ...currentUser,
      theme: activeTheme,
      themeMode,
      customTheme: {
        ...((currentUser?.customTheme as CustomThemeModes) || {}),
        [themeMode]: customColors,
      },
      useCustomTheme: true,
    };

    try {
      const res = await updatetDataById(users, updatedUser, currentUser._id);
      if (res.data) {
        saveUser(updatedUser);
        setCurrentUser(updatedUser);
        setUseCustomTheme(true);

        const customCssVars = buildCustomCssVars(customColors, themeMode);
        applyTheme(activeTheme, customCssVars, themeMode);

        setSnackbar({
          open: true,
          message: "Custom Theme Saved and Applied Successfully!",
          severity: "success",
        });
        setIsCustomizerOpen(false);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to save custom theme in database.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error saving custom theme:", error);
      setSnackbar({
        open: true,
        message: "An error occurred while saving custom theme.",
        severity: "error",
      });
    } finally {
      setLoadingTheme(false);
    }
  };

  // Reset custom colors back to base theme defaults
  const handleResetColors = () => {
    const defaultColors = getDefaultColorsForTheme(activeTheme, themeMode);
    setCustomColors(defaultColors);
    if (useCustomTheme) {
      const customCssVars = buildCustomCssVars(defaultColors, themeMode);
      applyTheme(activeTheme, customCssVars, themeMode);
    }
  };

  return (
    <div className="mt-3">
      <Card extra={"w-full sm:overflow-auto px-8 mt-5 py-10"}>
        <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h4 className="text-xl font-bold text-textPrimary">
              Customization &amp; Themes
            </h4>
            <p className="mt-1 text-base text-textSecondary">
              Select a preset theme or customize individual theme colors to fit
              your style.
            </p>
          </div>
          <div className="flex w-fit flex-wrap items-center justify-end gap-4">
            <div className="flex rounded-xl border border-borderPrimary bg-surfaceSecondary p-1" role="group" aria-label="Theme appearance">
              {(["light", "dark"] as ThemeMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => handleModeChange(mode)}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold capitalize transition ${themeMode === mode
                    ? "bg-brand-500 text-white"
                    : "text-textSecondary hover:text-textPrimary"
                    }`}
                >
                  {mode}
                </button>
              ))}
            </div>
            <button
              onClick={() => setIsCustomizerOpen(true)}
              className="linear flex items-center justify-center rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition duration-200 hover:bg-brand-500/80 active:bg-brand-500/70"
            >
              <MdPalette className="mr-2 h-5 w-5" />
              Customize Theme
            </button>
          </div>
        </div>

        {/* Theme Presets */}
        <h5 className="text-md mb-4 font-bold text-textPrimary">
          Theme Presets
        </h5>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {THEME_OPTIONS.map((theme) => {
            const isActive = activeTheme === theme.id && !useCustomTheme;
            const previewColors = getDefaultColorsForTheme(theme.id, themeMode);

            return (
              <div
                key={theme.id}
                onClick={() => handleThemeSelect(theme.id)}
                className={`relative flex cursor-pointer flex-col justify-between rounded-2xl border-2 p-5 transition-all duration-300 hover:shadow-lg ${
                  isActive
                    ? "border-brand-500 bg-brand-50/20"
                    : "border-borderPrimary bg-surfacePrimary/60 hover:border-brand-500/80"
                }`}
              >
                <div>
                  <div className="flex items-center justify-between">
                    <h5 className="text-lg font-bold text-textPrimary">
                      {theme.name}
                    </h5>
                    {isActive && (
                      <MdCheckCircle className="h-6 w-6 text-brand-500" />
                    )}
                  </div>
                  <p className="mt-2 text-sm text-textSecondary">
                    {theme.description}
                  </p>
                  <div
                    className="mt-4 flex h-10 overflow-hidden rounded-lg border border-borderPrimary"
                    aria-label={`${theme.name} color preview`}
                  >
                    <span
                      className="flex-1"
                      style={{ backgroundColor: previewColors.bg }}
                    />
                    <span
                      className="flex-[2]"
                      style={{ backgroundColor: previewColors.primary }}
                    />
                    <span
                      className="flex-1"
                      style={{ backgroundColor: previewColors.secondary }}
                    />
                    <span
                      className="flex-1"
                      style={{ backgroundColor: previewColors.textPrimary }}
                    />
                  </div>
                </div>

                <div className="mt-6">
                  {isActive ? (
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-brand-500">
                      Currently Active
                    </span>
                  ) : (
                    <button
                      disabled={loadingTheme}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleThemeSelect(theme.id);
                      }}
                      className="linear flex items-center justify-center rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition duration-200 hover:bg-brand-500/80 active:bg-brand-500/70"
                    >
                      Apply Theme
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Color Customizer Modal */}
        <Modal
          open={isCustomizerOpen}
          onClose={() => setIsCustomizerOpen(false)}
        >
          <Box
            sx={{
              position: "absolute" as "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "95%",
              maxWidth: "1000px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: 24,
              borderRadius: "16px",
              outline: "none",
            }}
            className="border border-borderPrimary bg-surfacePrimary p-6"
          >
            <div className="relative">
              <button
                onClick={() => setIsCustomizerOpen(false)}
                className="absolute -right-2 -top-2 p-1 text-textSecondary hover:text-textPrimary"
              >
                <HiX className="h-6 w-6" />
              </button>

              <div className="flex flex-col justify-between gap-4 pr-8 sm:flex-row sm:items-center">
                <div>
                  <h5 className="flex items-center gap-2 text-lg font-bold text-textPrimary">
                    <MdPalette className="text-brand-500" />
                    Customize Colors for Base Theme (
                    <span className="capitalize">{activeTheme}</span>)
                  </h5>
                  <p className="mt-1 text-sm text-textSecondary">
                    Choose five base colors. All supporting colors are
                    calculated automatically for readable contrast.
                  </p>
                </div>

                <button
                  onClick={handleResetColors}
                  className="flex items-center gap-1 text-sm font-medium text-textSecondary hover:text-brand-500"
                >
                  <MdRestore className="h-4 w-4" />
                  Reset to Base Defaults
                </button>
              </div>

              <div className="mt-5 flex items-center justify-between rounded-xl border border-borderPrimary bg-surfaceSecondary p-3">
                <span className="text-sm font-semibold text-textPrimary">Editing {themeMode} appearance</span>
                <div className="flex rounded-lg border border-borderPrimary bg-surfacePrimary p-1" role="group" aria-label="Customize appearance mode">
                  {(["light", "dark"] as ThemeMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => handleModeChange(mode)}
                      className={`rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition ${themeMode === mode
                        ? "bg-brand-500 text-white"
                        : "text-textSecondary hover:text-textPrimary"
                        }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {COLOR_PICKER_FIELDS.map((field) => (
                  <div
                    key={field.key}
                    className="flex items-center justify-between rounded-xl border border-borderPrimary bg-surfaceSecondary p-4"
                  >
                    <div>
                      <label className="block text-sm font-semibold text-textPrimary">
                        {field.label}
                      </label>
                      
                    </div>
                    <input
                      type="color"
                      value={customColors[field.key] || field.defaultVal}
                      onChange={(e) =>
                        handleColorChange(field.key, e.target.value)
                      }
                      className="bg-transparent h-10 w-12 cursor-pointer rounded-lg border-0 p-0"
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  disabled={loadingTheme}
                  onClick={handleSaveCustomTheme}
                  className="linear flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-6 py-3 text-base font-semibold text-white shadow-md transition duration-200 hover:bg-brand-500/80 active:bg-brand-500/70"
                >
                  {loadingTheme ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    <>
                      <MdPalette className="h-5 w-5" />
                      Save &amp; Apply Custom Theme
                    </>
                  )}
                </button>
              </div>
            </div>
          </Box>
        </Modal>
      </Card>

      <AppSnackbar
        isOpen={snackbar.open}
        title={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
      />
    </div>
  );
};

export default ChangeTheme;
